<script setup lang="ts">
import { ref, toRefs, watch, useTemplateRef, computed } from '#imports';

import { useClickAway, ClickAwayMode } from '../../utils/useClickAway';

const props = withDefaults(defineProps<{
  visible?: boolean;
  isTransparent?: boolean;
  render?: boolean;
  clickable?: boolean;
}>(), {
  visible: false,
  isTransparent: false,
  render: true,
  clickable: true,
});

const emit = defineEmits(['click:end', 'click:external']);

const { visible, clickable } = toRefs(props);
const visibleBackground = ref(visible.value);
const isAnimateBackground = ref(visible.value);
const backgroundNode = useTemplateRef('backgroundNode');

const viewClass = computed(() => {
  const result = [];
  
  if (visibleBackground.value) {
    result.push('_is-visible');
  }
  
  if (isAnimateBackground.value) {
    result.push('_is-animate');
  }
  
  if (props.isTransparent) {
    result.push('_is-transparent');
  }
  
  if (clickable.value) {
    result.push('_clickable');
  }
  
  return result;
});

function onEscapeKeyUp(event: KeyboardEvent) {
  if (event.which === 27) {
    emit('click:end');
  }
}

watch(visible, (value) => {
  controlVisibleState(value);
});

/** Меняет видимость фона в зависимости от входящих данных */
function controlVisibleState(value: boolean) {
  if (value) {
    visibleBackground.value = true;
    isAnimateBackground.value = true;
    
    window.addEventListener('keyup', onEscapeKeyUp);
  } else {
    isAnimateBackground.value = false;
    
    setTimeout(() => {
      visibleBackground.value = false;
    }, 300);
    
    window.removeEventListener('keyup', onEscapeKeyUp);
  }
}

const { clickAwayEvent } = useClickAway(backgroundNode, () => emit('click:external'), clickable, ClickAwayMode.Equal);
</script>

<template lang="pug">
.ui-background-abstract(
  ref="backgroundNode"
  :class="viewClass"
  @click="clickAwayEvent"
)
  .content
    template(v-if="render")
      slot
    template(v-else)
      slot(v-if="visible")
</template>

<style lang="scss" scoped>
.ui-background-abstract {
  visibility: hidden;
  position: fixed;
  justify-content: center;
  align-items: center;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(15, 0, 30, 0.2);
  z-index: 1050;
  display: flex;
  opacity: 0;
  transition: opacity ease-out 0.3s;

  &._is-visible {
    visibility: visible;
  }

  &._is-animate {
    opacity: 1;
    transition: opacity ease-out 0.3s;
  }

  &._is-transparent {
    background-color: transparent;
  }

  &._clickable {
    cursor: pointer;
  }
}

.content {
  cursor: default;
  display: flex;
}
</style>
